import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Download, Trash2, PenLine, Eraser } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { brl, fmtDate, statusLabel, statusColor } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BASE = API.replace(/\/api$/, "");

const EMPTY = {
  customer_id: "", equipment_id: "", address: "", technician: "", scheduled_date: "",
  problem: "", diagnosis: "", service_done: "", parts: [], refrigerant: "", notes: "",
  warranty: "90 dias", value: 0, status: "agendada", photos: [],
};

function SignatureDialog({ os, onClose, onSaved }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [signer, setSigner] = useState("");
  const [saving, setSaving] = useState(false);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (canvasRef.current.width / r.width), y: (e.clientY - r.top) * (canvasRef.current.height / r.height) };
  };
  const start = (e) => {
    drawing.current = true;
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(p.x, p.y);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.stroke();
    setHasInk(true);
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasInk(false);
  };

  const save = async () => {
    if (!hasInk) { toast.error("Desenhe a assinatura do cliente."); return; }
    setSaving(true);
    try {
      const blob = await new Promise((res) => canvasRef.current.toBlob(res, "image/png"));
      const fd = new FormData();
      fd.append("file", blob, "assinatura.png");
      fd.append("context", "assinaturas");
      const { data } = await api.post("/files/upload", fd);
      await api.put(`/work-orders/${os.id}`, {
        ...os, signature_url: data.url, signed_by: signer, signed_at: new Date().toISOString(),
      });
      toast.success("Assinatura do cliente registrada!");
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="os-sign-dialog">
        <DialogHeader><DialogTitle>Assinatura do cliente — {os.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Nome de quem assina</Label>
            <Input className="h-12" data-testid="os-sign-name" value={signer} onChange={(e) => setSigner(e.target.value)} placeholder="Nome do cliente" />
          </div>
          <div className="border border-slate-300 rounded-lg bg-white touch-none">
            <canvas ref={canvasRef} width={880} height={300} className="w-full h-40 cursor-crosshair"
              data-testid="os-sign-canvas"
              onPointerDown={start} onPointerMove={move} onPointerUp={end} onPointerLeave={end} />
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="h-11" onClick={clear} data-testid="os-sign-clear">
              <Eraser className="h-4 w-4 mr-1" /> Limpar
            </Button>
            <Button type="button" className="flex-1 h-11 bg-sky-600 hover:bg-sky-700 text-white" onClick={save} disabled={saving} data-testid="os-sign-save">
              {saving ? "Salvando..." : "Salvar assinatura"}
            </Button>
          </div>
          <p className="text-[11px] text-slate-400">A assinatura é anexada ao PDF da OS junto com data e nome.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function WorkOrders() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [sigOs, setSigOs] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/work-orders").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.customer_id) {
      api.get(`/customers/${form.customer_id}`).then((r) => setEquipment(r.data.equipment || [])).catch(() => {});
    }
  }, [form.customer_id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => { setEditing(o.id); setForm({ ...EMPTY, ...o }); setOpen(true); };

  const uploadOsPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("context", "os");
      const { data } = await api.post("/files/upload", fd);
      setForm((cur) => ({ ...cur, photos: [...(cur.photos || []), data.url] }));
      toast.success("Foto anexada à OS.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, value: Number(form.value) || 0 };
      if (editing) await api.put(`/work-orders/${editing}`, payload);
      else await api.post("/work-orders", payload);
      toast.success(editing ? "OS atualizada!" : "OS criada!");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir esta OS?")) return;
    await api.delete(`/work-orders/${id}`);
    load();
  };

  return (
    <div className="space-y-5 animate-in-up" data-testid="workorders-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Ordens de serviço</h1>
          <p className="text-sm text-slate-500">{items.length} no total</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={openNew} data-testid="new-os-btn">
              <Plus className="h-4 w-4 mr-2" /> Nova OS
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Editar OS" : "Nova ordem de serviço"}</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3">
              <div className="space-y-1.5">
                <Label>Cliente *</Label>
                <Select value={form.customer_id} onValueChange={(v) => set("customer_id")(v)} required>
                  <SelectTrigger className="h-12" data-testid="os-customer"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Equipamento</Label>
                <Select value={form.equipment_id} onValueChange={(v) => set("equipment_id")(v)} disabled={!equipment.length}>
                  <SelectTrigger className="h-12" data-testid="os-equipment"><SelectValue placeholder={equipment.length ? "Selecione" : "Sem equipamentos"} /></SelectTrigger>
                  <SelectContent>{equipment.map((e) => <SelectItem key={e.id} value={e.id}>{[e.type, e.brand, e.model].filter(Boolean).join(" · ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Endereço do serviço</Label><Input className="h-12" data-testid="os-address" value={form.address} onChange={set("address")} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Técnico</Label><Input className="h-12" data-testid="os-technician" value={form.technician} onChange={set("technician")} /></div>
                <div className="space-y-1.5"><Label>Data</Label><Input className="h-12" type="date" data-testid="os-date" value={form.scheduled_date} onChange={set("scheduled_date")} /></div>
              </div>
              <div className="space-y-1.5"><Label>Problema relatado</Label><Textarea data-testid="os-problem" value={form.problem} onChange={set("problem")} rows={2} /></div>
              <div className="space-y-1.5"><Label>Diagnóstico</Label><Textarea data-testid="os-diagnosis" value={form.diagnosis} onChange={set("diagnosis")} rows={2} /></div>
              <div className="space-y-1.5"><Label>Serviço realizado</Label><Textarea data-testid="os-service-done" value={form.service_done} onChange={set("service_done")} rows={2} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Refrigerante utilizado</Label><Input className="h-12" data-testid="os-refrigerant" value={form.refrigerant} onChange={set("refrigerant")} placeholder="Ex: R410A 1,5 kg" /></div>
                <div className="space-y-1.5"><Label>Valor (R$)</Label><Input className="h-12 font-mono-num" type="number" step="0.01" data-testid="os-value" value={form.value} onChange={set("value")} /></div>
                <div className="space-y-1.5"><Label>Garantia</Label><Input className="h-12" data-testid="os-warranty" value={form.warranty} onChange={set("warranty")} /></div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => set("status")(v)}>
                    <SelectTrigger className="h-12" data-testid="os-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agendada">Agendada</SelectItem>
                      <SelectItem value="em_andamento">Em andamento</SelectItem>
                      <SelectItem value="concluida">Concluída</SelectItem>
                      <SelectItem value="cancelada">Cancelada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Observações</Label><Input className="h-12" data-testid="os-notes" value={form.notes} onChange={set("notes")} /></div>
              <div className="space-y-1.5">
                <Label>Fotos do serviço</Label>
                <Input type="file" accept="image/*" className="h-12" onChange={uploadOsPhoto} disabled={uploading} data-testid="os-photo-input" />
                {(form.photos || []).length > 0 && (
                  <div className="flex gap-2 flex-wrap pt-1">
                    {form.photos.map((u, i) => (
                      <img key={i} src={`${BASE}${u}`} alt={`Foto ${i + 1}`} className="h-16 w-16 rounded-lg object-cover border border-slate-200" data-testid={`os-photo-preview-${i}`} />
                    ))}
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="os-save-btn">Salvar OS</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3">
        {items.length === 0 && (
          <Card className="border-slate-200"><CardContent className="p-8 text-center text-slate-500 text-sm">
            Nenhuma ordem de serviço. Crie a primeira!
          </CardContent></Card>
        )}
        {items.map((o) => (
          <Card key={o.id} className="border-slate-200 shadow-sm" data-testid={`os-card-${o.number}`}>
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <button className="flex-1 min-w-0 text-left" onClick={() => openEdit(o)} data-testid={`os-open-${o.number}`}>
                <p className="font-semibold flex items-center gap-2 flex-wrap">
                  {o.number} · {o.customer_name}
                  {o.signature_url && <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200 bg-emerald-50" data-testid={`os-signed-${o.number}`}>Assinada</Badge>}
                  {(o.photos || []).length > 0 && <Badge variant="outline" className="text-[10px]" data-testid={`os-photos-${o.number}`}>{o.photos.length} foto(s)</Badge>}
                </p>
                <p className="text-sm text-slate-500 truncate">{o.problem || o.service_done || "Serviço"} · {fmtDate(o.scheduled_date)}</p>
              </button>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-right">
                  <p className="font-mono-num text-sm font-semibold">{brl(o.value)}</p>
                  <Badge variant="outline" className={`text-[10px] ${statusColor[o.status]}`}>{statusLabel[o.status]}</Badge>
                </div>
                <Button variant="ghost" size="icon" className="h-10 w-10 text-sky-700 hover:bg-sky-50" title="Assinatura do cliente" onClick={() => setSigOs(o)} data-testid={`os-sign-${o.number}`}>
                  <PenLine className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => window.open(`${API}/work-orders/${o.id}/pdf`, "_blank")} data-testid={`os-pdf-${o.number}`}>
                  <Download className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-10 w-10 text-red-500 hover:bg-red-50 hover:text-red-700" onClick={() => remove(o.id)} data-testid={`os-delete-${o.number}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {sigOs && <SignatureDialog os={sigOs} onClose={() => setSigOs(null)} onSaved={() => { setSigOs(null); load(); }} />}
    </div>
  );
}
